import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import Button from '../ui/Button';
import { Label } from '../pilot-ui/label';
import { Textarea } from '../pilot-ui/textarea';
import { formatTimestamp } from '../../utils/formatTimestamp';
import { useAuth } from '../../context/AuthContext';
import { notifyNoteRead } from '../../hooks/useUnreadNoteCount';
import {
  AppUser,
  SendResult,
  Thread,
  apiCode,
  apiMessage,
  category,
  appUserApi,
  identity,
} from './api';

function pushMessage(result: SendResult): string {
  if (result.push.status === 'sent') return 'Reply saved. Push sent.';
  if (result.push.status === 'failed') return 'Reply saved. Push failed.';
  const reason = {
    push_disabled: 'push is off',
    no_token: 'there is no device token',
    duplicate: 'this was a duplicate',
  }[result.push.reason ?? ''];
  return `Reply saved. Push skipped because ${reason || result.push.reason || 'delivery was unavailable'}.`;
}

function subject(account: AppUser, thread: Thread): string {
  const home = account.locations.find((item) => item.id === thread.locationId);
  const systems = account.locations.flatMap((item) => item.hvacSystems);
  const system =
    home?.hvacSystems.find((item) => item.id === thread.hvacSystemId) ||
    systems.find((item) => item.id === thread.hvacSystemId);
  const clip =
    system?.filters.find((item) => item.id === thread.filterId) ||
    systems
      .flatMap((item) => item.filters)
      .find((item) => item.id === thread.filterId);
  return [
    home?.name || home?.id || thread.locationId,
    system?.name || system?.id || thread.hvacSystemId,
    clip?.name || clip?.location || clip?.deviceName || thread.filterId,
  ]
    .filter(Boolean)
    .join(' → ');
}

export default function AppUserThreadPage() {
  const { uid = '', noteId = '' } = useParams();
  const { id: accountId, hasPermission, scopes } = useAuth();
  const canRead = hasPermission('firestore_read');
  const canSend = hasPermission('send_notifications');
  const hasFullScope = scopes.includes('/');
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [replyError, setReplyError] = useState('');
  const [readError, setReadError] = useState('');
  const [result, setResult] = useState('');
  const [contactBlocked, setContactBlocked] = useState(false);
  const [sending, setSending] = useState(false);
  const sendBusy = useRef(false);
  const composition = useRef({ text: '', requestId: '', attempted: false });
  const loadRun = useRef(0);

  useEffect(() => {
    const run = ++loadRun.current;
    sendBusy.current = false;
    setSending(false);
    setText('');
    composition.current = { text: '', requestId: '', attempted: false };
    setReplyError('');
    setReadError('');
    setResult('');
    setContactBlocked(false);
    setLoading(true);
    setError('');
    setAppUser(null);
    setThread(null);
    if (!canRead || !hasFullScope) return;
    Promise.all([
      appUserApi.overview(uid),
      appUserApi.thread(uid, noteId),
    ])
      .then(([account, note]) => {
        if (loadRun.current !== run) return;
        setAppUser(account);
        setThread(note);
        const newestReply = [...note.responses].reverse()
          .find(response => response.authorType === 'user');
        const throughMessageId = newestReply?.id ??
          ((note.authorType ?? 'user') === 'user' ? note.id : null);
        if (throughMessageId) {
          void appUserApi.markRead(uid, noteId, throughMessageId)
            .then(outcome => {
              notifyNoteRead();
              if (loadRun.current !== run) return;
              setThread(current => current
                ? { ...current, unread: outcome.unread }
                : current);
            })
            .catch(() => {
              if (loadRun.current === run) setReadError('Could not mark this note read.');
            });
        } else if (note.unread) {
          setReadError('Could not mark this note read.');
        }
      })
      .catch((cause) => {
        if (loadRun.current === run) setError(apiMessage(cause));
      })
      .finally(() => {
        if (loadRun.current === run) setLoading(false);
      });
    return () => {
      loadRun.current++;
    };
  }, [uid, noteId, accountId, canRead, canSend, hasFullScope]);

  const disabledReason = !canSend
    ? 'Replying requires the send_notifications permission.'
    : appUser && !appUser.hasUserDoc
      ? 'Contact is unavailable because this app user has no app profile.'
      : contactBlocked || (appUser && !appUser.notesContactConsentEffective)
        ? 'The app user turned off contact from the team.'
        : null;
  const personName = appUser ? identity(appUser) : uid;

  function edit(value: string) {
    if (value !== composition.current.text) {
      const requestId =
        composition.current.attempted || !composition.current.requestId
          ? crypto.randomUUID()
          : composition.current.requestId;
      composition.current = { text: value, requestId, attempted: false };
    }
    setText(value);
    setReplyError('');
    setResult('');
  }

  async function send() {
    if (sendBusy.current || disabledReason) return;
    if (!text.trim()) {
      setReplyError('A reply is needed.');
      return;
    }
    if (text.trim().length > 2000) {
      setReplyError('A reply must be 2000 characters or fewer.');
      return;
    }
    sendBusy.current = true;
    setSending(true);
    setReplyError('');
    setResult('');
    const sentText = text.trim();
    if (!composition.current.requestId)
      composition.current = {
        text,
        requestId: crypto.randomUUID(),
        attempted: false,
      };
    composition.current.attempted = true;
    const requestId = composition.current.requestId;
    const routeRun = loadRun.current;
    try {
      const outcome = await appUserApi.send(
        uid,
        noteId,
        sentText,
        requestId,
      );
      if (loadRun.current !== routeRun) return;
      setResult(pushMessage(outcome));
      try {
        const refreshed = await appUserApi.thread(uid, noteId);
        if (loadRun.current !== routeRun) return;
        setThread(refreshed);
        setText('');
        composition.current = {
          text: '',
          requestId: crypto.randomUUID(),
          attempted: false,
        };
      } catch (refreshError) {
        if (loadRun.current === routeRun)
          setReplyError(
            `Reply saved, but the thread could not refresh: ${apiMessage(refreshError)}`,
          );
      }
    } catch (cause) {
      if (loadRun.current !== routeRun) return;
      if (apiCode(cause) === 'contact_off') {
        setContactBlocked(true);
        try {
          const account = await appUserApi.overview(uid);
          if (loadRun.current !== routeRun) return;
          setAppUser(account);
          setReplyError(
            'Contact is off. The app user turned off contact from the team.',
          );
        } catch (refreshError) {
          if (loadRun.current === routeRun)
            setReplyError(
              `Contact is off. Could not refresh the contact setting: ${apiMessage(refreshError)}`,
            );
        }
      } else {
        setReplyError(apiMessage(cause));
      }
    } finally {
      if (loadRun.current === routeRun) {
        sendBusy.current = false;
        setSending(false);
      }
    }
  }

  if (!canRead || !hasFullScope)
    return (
      <main className="mx-auto max-w-6xl p-6">
        <h1 className="text-2xl font-semibold">Note from {personName}</h1>
        <p role="alert">{!canRead
          ? 'Viewing App Users requires the firestore_read permission.'
          : 'Viewing App Users requires full device scope (/).'}</p>
      </main>
    );
  return (
    <main className="mx-auto max-w-6xl space-y-8 p-6">
      <Link
        to={`/app-users/${encodeURIComponent(uid)}`}
        className="text-primary underline"
        title="Return to this app user's account and notes"
      >
        ← {personName}
      </Link>
      <h1 className="text-2xl font-semibold">Note from {personName}</h1>
      {loading && <p role="status">Loading note thread…</p>}
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      {readError && <p role="alert" className="text-sm text-destructive">{readError}</p>}
      {appUser && thread && (
        <>
          <section
            aria-labelledby="note-heading"
            className="space-y-2 rounded-lg border border-border bg-card p-4"
          >
            <h2 id="note-heading" className="text-lg font-semibold">
              {category(thread.type)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {formatTimestamp(thread.createdAt)} · {identity(appUser)}
            </p>
            {subject(appUser, thread) && (
              <p>About: {subject(appUser, thread)}</p>
            )}
            {thread.systemText && (
              <p className="whitespace-pre-wrap break-words">
                {thread.systemText}
              </p>
            )}
            {thread.userText && (
              <p className="whitespace-pre-wrap break-words">
                {thread.userText}
              </p>
            )}
            {!thread.systemText && !thread.userText && <p>No text</p>}
            {thread.needsReply && <p className="font-medium">Needs reply</p>}
          </section>
          <section aria-labelledby="thread-heading" className="space-y-3">
            <h2 id="thread-heading" className="text-lg font-semibold">
              Thread
            </h2>
            {thread.truncated && (
              <p role="status">
                Thread truncated by the API; showing the responses it returned.
              </p>
            )}
            {!thread.responses.length && <p>No responses yet.</p>}
            {thread.responses.length > 0 && (
              <>
                <p id="note-response-scroll-help" className="text-sm text-muted-foreground">
                  {thread.responses.length} {thread.responses.length === 1 ? 'response' : 'responses'} · scroll to read the full thread
                </p>
                <ol
                  className="max-h-screen space-y-3 overflow-y-auto"
                  tabIndex={0}
                  aria-label="Note responses"
                  aria-describedby="note-response-scroll-help"
                  title="Scroll through the note responses, oldest first"
                >
                  {thread.responses.map((response) => (
                    <li
                      key={response.id}
                      className="rounded-lg border border-border bg-card p-4"
                    >
                      <p className="font-medium">
                        {response.authorType === 'user'
                          ? identity(appUser)
                          : 'MyFluxHive'}
                        {response.authorType === 'team' && response.sentByEmail
                          ? ` · sent by ${response.sentByEmail}`
                          : ''}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatTimestamp(response.createdAt)}
                      </p>
                      <p className="mt-2 whitespace-pre-wrap break-words">
                        {response.text}
                      </p>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
          <section
            aria-labelledby="reply-heading"
            className="space-y-3 rounded-lg border border-border bg-card p-4"
          >
            <h2 id="reply-heading" className="text-lg font-semibold">
              Reply
            </h2>
            <Label htmlFor="app-user-reply">
              Reply to {identity(appUser)}
            </Label>
            <p
              id="app-user-reply-help"
              className="text-sm text-muted-foreground"
            >
              Write a team reply. Sending saves it to this note and may notify
              the app user.
            </p>
            {disabledReason && (
              <p
                id="app-user-reply-disabled"
                className="text-sm text-destructive"
              >
                {disabledReason}
              </p>
            )}
            <Textarea
              id="app-user-reply"
              value={text}
              onChange={(event) => edit(event.target.value)}
              maxLength={2000}
              disabled={Boolean(disabledReason) || sending}
              aria-describedby={`app-user-reply-help app-user-reply-count${disabledReason ? ' app-user-reply-disabled' : ''}${replyError ? ' app-user-reply-error' : ''}`}
              title="Write a reply of up to 2000 characters to this app user"
            />
            <p
              id="app-user-reply-count"
              className="text-sm text-muted-foreground"
            >
              {text.length}/2000 characters
            </p>
            {replyError && (
              <p
                id="app-user-reply-error"
                role="alert"
                className="text-destructive"
              >
                {replyError}
              </p>
            )}
            {result && <p role="status">{result}</p>}
            <Button
              variant="primary"
              onClick={send}
              disabled={Boolean(disabledReason) || sending}
              aria-describedby={
                disabledReason ? 'app-user-reply-disabled' : undefined
              }
              title="Send this reply to the app user's note and report the push result"
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </section>
        </>
      )}
    </main>
  );
}
